import { DOMAINS, REASONS } from '@ads-bread/shared/bread/util';
import { PaymentsContext } from './paymentsMachine';
import {
  FetchPaymentMethodResult,
  FetchPaymentMethodsResult,
  CreatePaymentMethodResult,
  EvaluateErrorResponseResult,
} from './types/actors';
import { PaymentMachineEvents } from './types/events';
import { getPaymentMethodError } from './utils';

type PaymentsGuardArgs = {
  context: PaymentsContext;
  event: PaymentMachineEvents;
};

/**
 * Guard function to check if the response contains a payment error
 * @param _ PaymentsGuardArgs
 * @param params FetchPaymentMethodResult | CreatePaymentMethodResult | FetchPaymentMethodsResult
 * @returns boolean
 */
export const isPaymentsErrorResponse = (
  _: PaymentsGuardArgs,
  params:
    | FetchPaymentMethodResult
    | FetchPaymentMethodsResult
    | CreatePaymentMethodResult
): boolean => {
  const error = getPaymentMethodError(params);
  return !!error;
};

/**
 * Guard function to check if the payment error is an invalid payment method error
 * @param _ PaymentsGuardArgs
 * @param params EvaluateErrorResponseResult
 * @returns boolean
 */
export const isInvalidPaymentMethodError = (
  _: PaymentsGuardArgs,
  params: EvaluateErrorResponseResult
): boolean => {
  const reason = params.reason;
  return reason === REASONS[DOMAINS.PAYMENTS].INVALID_PAYMENT_METHOD_ERROR;
};

/**
 * Guard function to check if the payment error is a write card account error
 * @param _ PaymentsGuardArgs
 * @param params EvaluateErrorResponseResult
 * @returns boolean
 */
export const isWriteCardAccountError = (
  _: PaymentsGuardArgs,
  params: EvaluateErrorResponseResult
): boolean => {
  const reason = params.reason;
  return reason === REASONS[DOMAINS.PAYMENTS].WRITE_CARD_ACCOUNT_ERROR;
};

/**
 * Guard function to check if the payment error is a get card connection error
 * @param _ PaymentsGuardArgs
 * @param params EvaluateErrorResponseResult
 * @returns boolean
 */
export const isGetCardConnectionError = (
  _: PaymentsGuardArgs,
  params: EvaluateErrorResponseResult
): boolean => {
  const reason = params.reason;
  return reason === REASONS[DOMAINS.PAYMENTS].GET_CARD_CONNECTION_ERROR;
};

/**
 * Guard function to check if the payment error is a transaction error
 * @param _ PaymentsGuardArgs
 * @param params EvaluateErrorResponseResult
 * @returns boolean
 */
export const isTransactionError = (
  _: PaymentsGuardArgs,
  params: EvaluateErrorResponseResult
): boolean => {
  const reason = params.reason;
  return reason === REASONS[DOMAINS.PAYMENTS].TRANSACTION_ERROR;
};

/**
 * Guard function to check if the payment error is a get payment method error
 * @param _ PaymentsGuardArgs
 * @param params EvaluateErrorResponseResult
 * @returns boolean
 */
export const isGetPaymentMethodError = (
  _: PaymentsGuardArgs,
  params: EvaluateErrorResponseResult
): boolean => {
  const reason = params.reason;
  return reason === REASONS[DOMAINS.PAYMENTS].GET_PAYMENT_METHOD_ERROR;
};

/**
 * Guard function to check if the payment error is an authorize payment error
 * @param _ PaymentsGuardArgs
 * @param params EvaluateErrorResponseResult
 * @returns boolean
 */
export const isAuthorizePaymentError = (
  _: PaymentsGuardArgs,
  params: EvaluateErrorResponseResult
): boolean => {
  const reason = params.reason;
  return reason === REASONS[DOMAINS.PAYMENTS].AUTHORIZE_PAYMENT_ERROR;
};

/**
 * Guard function to check if the payment error is a card type mismatch error
 * @param _ PaymentsGuardArgs
 * @param params EvaluateErrorResponseResult
 * @returns boolean
 */
export const isCardTypeMismatchError = (
  _: PaymentsGuardArgs,
  params: EvaluateErrorResponseResult
): boolean => {
  const reason = params.reason;
  return reason === REASONS[DOMAINS.PAYMENTS].CARD_TYPE_MISMATCH_ERROR;
};
